import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { mdiLayersTripleOutline } from "@mdi/js";
import { IoAdd, IoBatteryFullSharp, IoBatteryHalfSharp, IoChevronBack, IoChevronForward, IoClose } from "react-icons/io5";
import Main from "../../../components/layout/Main";
import AppModal from "../../../components/layout/app/AppModal";
import AppSwitch from "../../../components/layout/app/AppSwitch";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { useToggleTop } from "../../../components/common/ToggleTop";
import useAlive from "../../../hooks/useAlive";
import { MODELS, isModel } from "../../../constants/devices";
import { BusyOverlay, DEFAULT_ERROR } from "../settings/shared";
import {
  ACCESSORY_PATHS,
  KINDS,
  PAIRING_PHOTOS,
  READ_DELAY_MS,
  SETTLE_MS,
  WAIT,
  batteryOf,
  countCommand,
  countOf,
  forgetList,
  idOf,
  imageStyle,
  isEnabled,
  pageCommand,
  pageCount,
  pageItems,
  pageRequest,
  pairingCommand,
  readRecord,
  rememberList,
  remoteType,
  restoreList,
  sendCommand,
  sensorCommand,
  sensorState,
  sensorType,
  wait,
  zoneLabel,
} from "./accessories";
import "../../../assets/styles/app-security-accessories.css";

const TABS = [
  { kind: KINDS.SENSOR, label: "Sensors", one: "sensor", many: "sensors" },
  { kind: KINDS.REMOTE, label: "Remote Control", one: "remote", many: "remotes" },
];
const PANEL_ID = "hx-acc-panel";
const tabId = (kind) => `hx-acc-tab-${kind}`;
const rowId = (kind, id) => `hx-acc-row-${kind}-${id}`;
const LOADING = { status: "loading", items: null };

// "Normal Zone | Close", with the bar read as a pause rather than a symbol.
const ZoneLine = ({ sensor }) => {
  const state = sensorState(sensor);
  return (
    <span className="hx-acc-zone">
      {zoneLabel(sensor)} Zone
      {state ? (
        <>
          <span aria-hidden="true"> | </span>
          <span className="hx-acc-sr">, </span>
          {state}
        </>
      ) : null}
    </span>
  );
};

// The accessories of a WZLTEG_1 security alarm gateway: the app's full-screen
// modal behind the layers icon (app/screens/SecurityDevices.js). Sensors and
// Remote Control tabs, five to a page, each page read from the gateway; a
// sensor's switch sets its acc_wm, and "+" keeps the gateway in pairing mode
// while the reference photos are open.
// Location state: { data: the gateway's config device }.
function SecurityAccessories() {
  const location = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = location.state?.data;
  const supported = isModel(device, MODELS.WZLTEG_1);

  // Back from an accessory, the list comes back as it was.
  const [initial] = useState(() => (supported ? restoreList(location.key, device) : null));
  const resumed = Boolean(initial && !initial.reload);
  const [view, setView] = useState({ kind: initial?.kind ?? KINDS.SENSOR, page: initial?.page ?? 1 });
  const [counts, setCounts] = useState(initial?.counts ?? { [KINDS.SENSOR]: null, [KINDS.REMOTE]: null });
  // status "loading" | "ready" | "error"; items is the page's accessories once ready.
  const [list, setList] = useState(resumed ? { status: "ready", items: initial.items ?? [] } : LOADING);
  const [busy, setBusy] = useState(supported && !resumed ? WAIT : null);
  const [pairing, setPairing] = useState(false);
  // Each read gets a number, so an earlier, slower read can't overwrite a later one.
  const reads = useRef(0);
  const pairingStart = useRef(null);
  const pairingOpen = useRef(false);
  pairingOpen.current = pairing;

  // The app's fetchData: the count, then the page's ids. Unlike the app, a
  // failed read ends the loader and offers Try again.
  const load = useCallback(
    async (kind, page) => {
      const read = ++reads.current;
      const current = () => alive.current && reads.current === read;
      setView({ kind, page });
      setList(LOADING);
      setBusy(WAIT);
      try {
        await sendCommand(device, countCommand(kind));
        await wait(READ_DELAY_MS);
        if (!current()) return;
        const count = countOf(await readRecord(device), kind);
        if (!current()) return;
        // After a delete the page can be past the end.
        const target = Math.min(page, pageCount(count));
        setCounts((known) => ({ ...known, [kind]: count }));
        setView({ kind, page: target });
        let items = [];
        if (count > 0) {
          await sendCommand(device, pageCommand(kind, pageRequest(target, count)));
          await wait(READ_DELAY_MS);
          if (!current()) return;
          items = pageItems(await readRecord(device), kind, target);
          if (!current()) return;
        }
        setList({ status: "ready", items });
      } catch (error) {
        if (current()) setList({ status: "error", items: null });
      } finally {
        if (current()) setBusy(null);
      }
    },
    [alive, device]
  );

  useEffect(() => {
    if (!supported) return;
    forgetList();
    if (!resumed) load(view.kind, view.page);
    else if (initial.focusId) document.getElementById(initial.focusId)?.focus();
    // Once, for the list as it was opened or restored.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving the page with the pairing dialog open takes the gateway out of
  // pairing mode, as closing the dialog does.
  useEffect(
    () => () => {
      if (pairingOpen.current) sendCommand(device, pairingCommand(false)).catch(() => {});
    },
    [device]
  );

  if (!device) return <Redirect to="/dashboard" />;

  const tab = TABS.find((item) => item.kind === view.kind);
  const count = counts[view.kind];
  const pages = pageCount(count);
  const { status, items } = list;

  const reload = () => {
    if (!busy) load(view.kind, view.page);
  };

  const chooseTab = (kind) => {
    if (busy || kind === view.kind) return;
    load(kind, 1);
  };

  const onTabKey = (event) => {
    const index = TABS.indexOf(tab);
    const next = {
      ArrowRight: (index + 1) % TABS.length,
      ArrowLeft: (index + TABS.length - 1) % TABS.length,
      Home: 0,
      End: TABS.length - 1,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    if (busy) return;
    chooseTab(TABS[next].kind);
    document.getElementById(tabId(TABS[next].kind))?.focus();
  };

  const goToPage = (page) => {
    if (busy || page < 1 || page > pages || page === view.page) return;
    load(view.kind, page);
  };

  // The app's toggleSensor: the sensor's whole record with the new acc_wm, and
  // the loader for 2 s. The switch goes back if the command fails.
  const toggle = async (sensor) => {
    if (busy) return;
    const id = String(sensor.acc_id);
    const next = isEnabled(sensor) ? 0 : 1;
    const setWm = (acc_wm) =>
      setList((current) =>
        current.items
          ? { ...current, items: current.items.map((item) => (String(item.acc_id) === id ? { ...item, acc_wm } : item)) }
          : current
      );
    setWm(String(next));
    setBusy(WAIT);
    try {
      await sendCommand(device, sensorCommand({ ...sensor, acc_wm: next }));
      await wait(SETTLE_MS);
    } catch (error) {
      if (alive.current) setWm(sensor.acc_wm);
      toast(DEFAULT_ERROR, "red");
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  const openAccessory = (accessory) => {
    if (busy) return;
    rememberList({
      key: location.key,
      applianceId: device.applianceId,
      kind: view.kind,
      page: view.page,
      counts,
      items,
      focusId: rowId(view.kind, idOf(accessory, view.kind)),
    });
    history.push(ACCESSORY_PATHS.DETAIL, { data: device, accessory, kind: view.kind });
  };

  // "+" shows the photos and starts pairing (id 4, value 1) at the same time,
  // as the app does.
  const openPairing = () => {
    if (busy || pairing) return;
    setPairing(true);
    const request = sendCommand(device, pairingCommand(true));
    pairingStart.current = request;
    request.catch(() => {
      if (!alive.current || pairingStart.current !== request) return;
      pairingStart.current = null;
      setPairing(false);
      toast(DEFAULT_ERROR, "red");
    });
  };

  // Closing ends pairing (value 0) and reads the list again for the new
  // accessories.
  const closePairing = async () => {
    if (!pairingOpen.current) return;
    pairingOpen.current = false;
    setPairing(false);
    const start = pairingStart.current;
    pairingStart.current = null;
    setBusy(WAIT);
    try {
      // The stop mustn't overtake a start that is still on its way.
      if (start) await start.catch(() => {});
      await sendCommand(device, pairingCommand(false));
    } catch (error) {
      if (alive.current) setBusy(null);
      toast(DEFAULT_ERROR, "red");
      return;
    }
    if (alive.current) load(view.kind, view.page);
  };

  const sensorRow = (sensor) => {
    const id = String(sensor.acc_id);
    const type = sensorType(sensor);
    const on = isEnabled(sensor);
    const battery = batteryOf(sensor);
    const name = sensor.acc_name || "";
    return (
      <li key={id} className="hx-acc-card">
        <button
          type="button"
          id={rowId(KINDS.SENSOR, id)}
          className="hx-acc-open"
          aria-disabled={busy ? true : undefined}
          onClick={() => openAccessory(sensor)}
        >
          <span className={`hx-acc-tile ${on ? "" : "is-off"}`} aria-hidden="true">
            <span className="hx-acc-img" style={imageStyle(type.image)} />
          </span>
          <span className="hx-acc-info">
            <span className="hx-acc-name">{name}</span>
            <span className="hx-acc-type">{type.label}</span>
            <ZoneLine sensor={sensor} />
            <span className={`hx-acc-battery ${battery === "Low" ? "is-low" : ""}`}>
              {battery === "Low" ? <IoBatteryHalfSharp aria-hidden="true" /> : <IoBatteryFullSharp aria-hidden="true" />}
              <span className="hx-acc-sr">Battery </span>
              {battery}
            </span>
          </span>
        </button>
        <span className="hx-acc-toggle">
          <AppSwitch
            checked={on}
            onChange={() => toggle(sensor)}
            label={`${name || type.label} active`}
            knobColor="#ffffff"
            trackColor="#980204"
            className="hx-acc-switch"
          />
        </span>
      </li>
    );
  };

  const remoteRow = (remote) => {
    const id = String(remote.rm_id);
    const type = remoteType(remote);
    return (
      <li key={id} className="hx-acc-card is-remote">
        <button
          type="button"
          id={rowId(KINDS.REMOTE, id)}
          className="hx-acc-open"
          aria-disabled={busy ? true : undefined}
          onClick={() => openAccessory(remote)}
        >
          <span className="hx-acc-tile" aria-hidden="true">
            <span className="hx-acc-img" style={imageStyle(type.image)} />
          </span>
          <span className="hx-acc-info">
            <span className="hx-acc-name">{remote.rm_name || ""}</span>
            <span className="hx-acc-type">{type.label}</span>
          </span>
        </button>
      </li>
    );
  };

  let body;
  if (status === "loading") {
    body = (
      <div aria-hidden="true">
        <div className="hx-skel hx-acc-skel" />
        <div className="hx-skel hx-acc-skel" />
        <div className="hx-skel hx-acc-skel" />
      </div>
    );
  } else if (status === "error") {
    body = (
      <div className="hx-acc-message" role="alert">
        <p>Couldn't load the accessories.</p>
        <button type="button" className="hx-acc-btn" onClick={reload}>
          Try again
        </button>
      </div>
    );
  } else if (!items.length) {
    body = (
      <div className="hx-acc-message">
        <MdiIcon path={mdiLayersTripleOutline} size={110} />
        <p>No Devices Found</p>
        <button type="button" className="hx-acc-btn is-light" onClick={reload}>
          Refresh
        </button>
      </div>
    );
  } else {
    body = <ul className="hx-acc-list">{items.map(view.kind === KINDS.SENSOR ? sensorRow : remoteRow)}</ul>;
  }

  let summary = "";
  if (status === "ready") {
    summary = items.length
      ? `${items.length} ${items.length === 1 ? tab.one : tab.many}, page ${view.page} of ${pages}`
      : "No Devices Found";
  }

  return (
    <Main header="stack" title="Accessories" hideTabBar>
      <div className="hx-acc">
        {device.name ? <p className="hx-acc-caption">{device.name}</p> : null}

        {supported ? (
          <>
            <div className="hx-acc-tabs" role="tablist" aria-label="Accessories">
              {TABS.map((item) => {
                const selected = item.kind === view.kind;
                return (
                  <button
                    key={item.kind}
                    id={tabId(item.kind)}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls={PANEL_ID}
                    tabIndex={selected ? 0 : -1}
                    className={`hx-acc-tab ${selected ? "is-active" : ""}`}
                    onClick={() => chooseTab(item.kind)}
                    onKeyDown={onTabKey}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <section id={PANEL_ID} role="tabpanel" aria-labelledby={tabId(view.kind)} aria-busy={status === "loading"}>
              {count > 0 ? (
                <nav className="hx-acc-pager" aria-label={`${tab.label} pages`}>
                  <button
                    type="button"
                    className="hx-acc-page-btn"
                    aria-label="Previous page"
                    aria-disabled={view.page <= 1 || undefined}
                    onClick={() => goToPage(view.page - 1)}
                  >
                    <IoChevronBack aria-hidden="true" />
                  </button>
                  <p className="hx-acc-page">{`Page ${view.page} of ${pages}`}</p>
                  <button
                    type="button"
                    className="hx-acc-page-btn"
                    aria-label="Next page"
                    aria-disabled={view.page >= pages || undefined}
                    onClick={() => goToPage(view.page + 1)}
                  >
                    <IoChevronForward aria-hidden="true" />
                  </button>
                </nav>
              ) : null}
              {body}
            </section>

            <button
              type="button"
              className="hx-acc-add"
              aria-label="Add accessory"
              aria-haspopup="dialog"
              aria-disabled={busy ? true : undefined}
              onClick={openPairing}
            >
              <IoAdd aria-hidden="true" />
            </button>
          </>
        ) : (
          <div className="hx-acc-message">
            <p>Accessories aren't available for this device.</p>
          </div>
        )}
      </div>

      <p className="hx-acc-sr" role="status">
        {summary}
      </p>
      <BusyOverlay message={busy} />

      <AppModal open={pairing} onClose={closePairing} title="Activate the accessories for enrollment" className="hx-acc-pair">
        <button type="button" className="hx-icon-btn hx-acc-pair-close" aria-label="Close" onClick={closePairing}>
          <IoClose aria-hidden="true" />
        </button>
        <p className="hx-acc-pair-text">Kindly activate the accessories for enrollment using the reference photos below.</p>
        <ul className="hx-acc-pair-photos">
          {PAIRING_PHOTOS.map((photo) => (
            <li key={photo.src}>
              <img src={photo.src} alt={photo.alt} width="112" height="112" />
            </li>
          ))}
        </ul>
      </AppModal>
    </Main>
  );
}

export default SecurityAccessories;
